import {
  ClassFormatEnum,
  ClassSeedingEnum,
  ClassTypeEnum,
  GenderEnum,
  MatchPlayerFormatEnum,
  TeamCategoryTemplateEnum,
  TeamLineupDraftModeEnum,
} from './../types/enums';
import { DEFAULT_GROUP_STANDINGS_TIE_BREAKERS } from '../utils/groupStandingsTieBreakers';

export const teamLineupDraftModeData = [
  { value: TeamLineupDraftModeEnum.OPEN, label: 'creativeDetail-teamLineupDraftModeOpen' },
  { value: TeamLineupDraftModeEnum.BLIND_BULK, label: 'creativeDetail-teamLineupDraftModeBlindBulk' },
  { value: TeamLineupDraftModeEnum.COIN_TOSS, label: 'creativeDetail-teamLineupDraftModeCoinToss' },
  {
    value: TeamLineupDraftModeEnum.SEQUENTIAL_BLIND,
    label: 'creativeDetail-teamLineupDraftModeSequentialBlind',
  },
  {
    value: TeamLineupDraftModeEnum.CAPTAIN_ORDER_ONLY,
    label: 'creativeDetail-teamLineupDraftModeCaptainOrderOnly',
  },
  {
    value: TeamLineupDraftModeEnum.OFFICIAL_ONLY,
    label: 'creativeDetail-teamLineupDraftModeOfficialOnly',
  },
  {
    value: TeamLineupDraftModeEnum.HOME_PICKS_FIRST,
    label: 'creativeDetail-teamLineupDraftModeHomePicksFirst',
  },
  {
    value: TeamLineupDraftModeEnum.AWAY_PICKS_FIRST,
    label: 'creativeDetail-teamLineupDraftModeAwayPicksFirst',
  },
  { value: TeamLineupDraftModeEnum.SNAKE_DRAFT, label: 'creativeDetail-teamLineupDraftModeSnakeDraft' },
  {
    value: TeamLineupDraftModeEnum.PER_MATCH_COIN_TOSS,
    label: 'creativeDetail-teamLineupDraftModePerMatchCoinToss',
  },
];

const hiddenTeamLineupDraftModes = new Set([
  TeamLineupDraftModeEnum.SIMULTANEOUS_REVEAL,
  TeamLineupDraftModeEnum.CAPTAIN_ORDER_ONLY,
  TeamLineupDraftModeEnum.ROSTER_LOCK_FIRST,
]);

export const teamLineupDraftModeDataActive = teamLineupDraftModeData.filter(
  (item) => !hiddenTeamLineupDraftModes.has(item.value),
);

export const teamLineupDraftModeDescriptionKeyByValue: Partial<
  Record<TeamLineupDraftModeEnum, string>
> = {
  [TeamLineupDraftModeEnum.OPEN]: 'creativeDetail-teamLineupDraftModeOpenDesc',
  [TeamLineupDraftModeEnum.BLIND_BULK]: 'creativeDetail-teamLineupDraftModeBlindBulkDesc',
  [TeamLineupDraftModeEnum.COIN_TOSS]: 'creativeDetail-teamLineupDraftModeCoinTossDesc',
  [TeamLineupDraftModeEnum.SEQUENTIAL_BLIND]: 'creativeDetail-teamLineupDraftModeSequentialBlindDesc',
  [TeamLineupDraftModeEnum.OFFICIAL_ONLY]: 'creativeDetail-teamLineupDraftModeOfficialOnlyDesc',
  [TeamLineupDraftModeEnum.HOME_PICKS_FIRST]: 'creativeDetail-teamLineupDraftModeHomePicksFirstDesc',
  [TeamLineupDraftModeEnum.AWAY_PICKS_FIRST]: 'creativeDetail-teamLineupDraftModeAwayPicksFirstDesc',
  [TeamLineupDraftModeEnum.SNAKE_DRAFT]: 'creativeDetail-teamLineupDraftModeSnakeDraftDesc',
  [TeamLineupDraftModeEnum.PER_MATCH_COIN_TOSS]: 'creativeDetail-teamLineupDraftModePerMatchCoinTossDesc',
};

export function getTeamLineupDraftModeDescriptionKey(
  mode: number | null | undefined,
): string | undefined {
  if (mode == null) {
    return undefined;
  }
  return teamLineupDraftModeDescriptionKeyByValue[Number(mode) as TeamLineupDraftModeEnum];
}

export type LegacyTeamLineupDraftModeNote = 'simultaneous' | 'captainOrder' | 'rosterLock';

export function resolveTeamLineupDraftModeForForm(rawMode: number): {
  mode: number;
  legacyNote: LegacyTeamLineupDraftModeNote | null;
} {
  const mode = Number(rawMode);
  if (mode === TeamLineupDraftModeEnum.SIMULTANEOUS_REVEAL) {
    return { mode: TeamLineupDraftModeEnum.BLIND_BULK, legacyNote: 'simultaneous' };
  }
  if (mode === TeamLineupDraftModeEnum.CAPTAIN_ORDER_ONLY) {
    return { mode: TeamLineupDraftModeEnum.OPEN, legacyNote: 'captainOrder' };
  }
  if (mode === TeamLineupDraftModeEnum.ROSTER_LOCK_FIRST) {
    return { mode: TeamLineupDraftModeEnum.OPEN, legacyNote: 'rosterLock' };
  }
  return { mode, legacyNote: null };
}

export function normalizeTeamLineupDraftModeForSave(mode: number): number {
  const value = Number(mode);
  if (value === TeamLineupDraftModeEnum.SIMULTANEOUS_REVEAL) {
    return TeamLineupDraftModeEnum.BLIND_BULK;
  }
  if (
    value === TeamLineupDraftModeEnum.CAPTAIN_ORDER_ONLY ||
    value === TeamLineupDraftModeEnum.ROSTER_LOCK_FIRST
  ) {
    return TeamLineupDraftModeEnum.OPEN;
  }
  return value;
}

export const classTypesData = [
  { value: ClassTypeEnum.SINGLES, label: 'enum-classTypeSingles' },
  { value: ClassTypeEnum.DOUBLES, label: 'enum-classTypeDoubles' },
  { value: ClassTypeEnum.TEAMS, label: 'enum-classTypeTeams' },
  // { value: ClassTypeEnum.CUP, label: 'enum-classTypeCup' },
];

export const classFormatData = [
  {
    value: ClassFormatEnum.GROUP,
    label: 'enum-formatGroup',
    isGroup: true,
    supportsDoubles: true,
  },
  {
    value: ClassFormatEnum.BRACKET,
    label: 'enum-formatDoubleElimination',
    isGroup: false,
    supportsDoubles: true,
  },
  {
    value: ClassFormatEnum.SINGLE_BRACKET,
    label: 'enum-formatSingleElimination',
    isGroup: false,
    supportsDoubles: true,
  },
  {
    value: ClassFormatEnum.GROUP_BRACKET,
    label: 'enum-formatGroupBracket',
    isGroup: true,
    supportsDoubles: true,
  },
  {
    value: ClassFormatEnum.BOX_LEAGUE,
    label: 'enum-formatBoxLeague',
    isGroup: true,
    supportsDoubles: false,
  },
  {
    value: ClassFormatEnum.GROUP_ONLY,
    label: 'enum-formatGroupOnly',
    isGroup: true,
    supportsDoubles: true,
  },
  {
    value: ClassFormatEnum.BOX_LEAGUE_ELIM,
    label: 'enum-formatBoxLeagueElimination',
    isGroup: true,
    supportsDoubles: false,
  },
];

export const classSeedingData = [
  {
    value: ClassSeedingEnum.NORMAL,
    label: 'enum-seedNormal',
  },
  { value: ClassSeedingEnum.SNAKE, label: 'enum-seedSnake' },
];

export const matchPlayerFormatLabelByValue: Record<MatchPlayerFormatEnum, string> = {
  [MatchPlayerFormatEnum.SINGLES]: 'enum-matchPlayerFormatSingles',
  [MatchPlayerFormatEnum.DOUBLES]: 'enum-matchPlayerFormatDoubles',
};

export const categoryGenderLabelByValue: Record<GenderEnum, string> = {
  [GenderEnum.MALE]: 'enum-categoryGenderMale',
  [GenderEnum.FEMALE]: 'enum-categoryGenderFemale',
  [GenderEnum.MIXED]: 'enum-categoryGenderMixed',
  [GenderEnum.OPEN]: 'enum-categoryGenderOpen',
};

export const teamCategoryTemplateData = [
  {
    template: TeamCategoryTemplateEnum.MENS_SINGLES,
    name: 'Mens singles',
    labelKey: 'creativeDetail-mens singles',
    matchPlayerFormat: MatchPlayerFormatEnum.SINGLES,
    gender: GenderEnum.MALE,
  },
  {
    template: TeamCategoryTemplateEnum.WOMENS_SINGLES,
    name: 'Womens singles',
    labelKey: 'creativeDetail-womens singles',
    matchPlayerFormat: MatchPlayerFormatEnum.SINGLES,
    gender: GenderEnum.FEMALE,
  },
  {
    template: TeamCategoryTemplateEnum.MENS_DOUBLES,
    name: 'Mens doubles',
    labelKey: 'creativeDetail-mens doubles',
    matchPlayerFormat: MatchPlayerFormatEnum.DOUBLES,
    gender: GenderEnum.MALE,
  },
  {
    template: TeamCategoryTemplateEnum.WOMENS_DOUBLES,
    name: 'Womens doubles',
    labelKey: 'creativeDetail-womens doubles',
    matchPlayerFormat: MatchPlayerFormatEnum.DOUBLES,
    gender: GenderEnum.FEMALE,
  },
  {
    template: TeamCategoryTemplateEnum.MIXED_DOUBLES,
    name: 'Mixed doubles',
    labelKey: 'creativeDetail-mixed doubles',
    matchPlayerFormat: MatchPlayerFormatEnum.DOUBLES,
    gender: GenderEnum.MIXED,
  },
  {
    template: TeamCategoryTemplateEnum.OPEN_DOUBLES,
    name: 'Open doubles',
    labelKey: 'creativeDetail-open doubles',
    matchPlayerFormat: MatchPlayerFormatEnum.DOUBLES,
    gender: GenderEnum.OPEN,
  },
];

export enum SaleStatusOptionEnum {
  CLOSED = 'enum-saleStatusClosed',
  OPEN = 'enum-saleStatusOpen',
}

export const SaleStatusOptionWithLabel = [
  { label: SaleStatusOptionEnum.CLOSED, value: false },
  { label: SaleStatusOptionEnum.OPEN, value: true },
];

export enum SaleStateClassOptionEnum {
  HIDE = 'enum-saleStateClassHide',
  SHOW = 'enum-saleStateClassShow',
}

export const SaleStateClassOptionWithLabel = [
  { label: SaleStateClassOptionEnum.HIDE, value: false },
  { label: SaleStateClassOptionEnum.SHOW, value: true },
];

export const initialData = {
  name: '',
  type: 1,
  minPlayers: 0,
  maxPlayers: 16,
  minTeamRoster: 2,
  maxTeamRoster: 4,
  numberOfGroups: 1,
  boxLeagueNumOfRounds: 1,
  surface: 1,
  ball: 1,
  isOnSale: 'true',
  format: 1,
  roundScoring: 'true',
  price: 1,
  vat: 1,
  salesStatus: SaleStateClassOptionWithLabel[1].value,
  stateOfClass: SaleStateClassOptionWithLabel[0].value,
  teams: [],
  tournamentGroups: [],
  tournamentCategories: [],
  tournamentMatches: [],
  isHasSubClasses: false,
  isDupr: false,
  requiresLicense: false,
  requiresDuprPremium: false,
  moveBox: false,
  numberPlayersToBox: 1,
  groupStandingsTieBreakers: [...DEFAULT_GROUP_STANDINGS_TIE_BREAKERS],
  useInheritedGroupStandingsTieBreakers: true,
  teamMatchupScoring: { win: 3, draw: 1, loss: 0 },
  useInheritedTeamMatchupScoring: true,
  groupStandingsRulesVersion: 1,
  seedingType: 1,
  autoProgress: true,
  playBronzeMatch: true,
  tiebreakerEnabled: true,
  groupTiebreakerEnabled: false,
  tiebreakerFormat: 2,
  teamLineupDraftMode: TeamLineupDraftModeEnum.OPEN,
  teamMatchupSlotOrder: null,
};

export const saleStatusOption = ['enum-saleStatusClosed', 'enum-saleStatusOpen'];
export const saleStateClassOption = ['enum-saleStateClassHide', 'enum-saleStateClassShow'];
